/**
 * Component implement the input line for a React Hook Form.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2026
 */

import {
  Control,
  Controller,
  ControllerProps,
  FieldError,
  FieldPath,
  FieldValues,
  UseFormRegister,
} from 'react-hook-form'
import Password from './Password'
import './RhfFormInput.css'

/**
 * FormInput is reused across different react-hook-form forms, each
 * with its own set of fields. TFieldValues is the type parameter
 * that lets this one definition adapt to whichever form's fields it
 * is used with, inferred per call site from the `register`/`control`
 * passed in.
 *
 * Example: if a form's values type is FormAType (with fields
 * `fieldA1`, `fieldA2`), passing its `register` into FormInput infers
 * TFieldValues = FormAType there, so `id` only accepts `"fieldA1"` or
 * `"fieldA2"`. A separate form with values type FormBType (fields
 * `fieldB1`, `fieldB2`) passed at another call site infers
 * TFieldValues = FormBType there instead, so `id` only accepts
 * `"fieldB1"` or `"fieldB2"` for that call.
 *
 * Typing everything against FieldValues directly (react-hook-form's
 * base type, roughly `Record<string, any>`) instead of this generic
 * would lose that per-call-site check: `id` would degrade to a plain
 * `string`, and `register`/`control` from any form would be accepted
 * interchangeably, regardless of which form's fields `id` actually
 * names.
 *
 * TName narrows things one level further, from "one form" down to
 * "one specific field in that form" — needed because `controlRender`
 * relies on knowing exactly which field it is rendering.
 *
 * Without TName, `id: FieldPath<TFieldValues>` is the union of every
 * field name in the form (e.g. `"fieldA1" | "fieldA2"`), and
 * `controlRender`'s `field.value` would inherit that same lack of
 * narrowing: typed as the union of every field's value type (e.g.
 * `string | boolean`) instead of the one field actually being
 * rendered.
 *
 * TName is inferred per call from the literal string passed as
 * `id`. Calling this component with `id="fieldA2"` infers
 * TName = "fieldA2" for that call, which flows into
 * `ControllerProps<TFieldValues, TName>['render']`, narrowing
 * `field.value` there to exactly `FormAType["fieldA2"]` (e.g.
 * `boolean`) rather than the union of every field's type.
 *
 * TName defaults to `FieldPath<TFieldValues>` (the full union) so
 * the plain `<input>` path (no `controlRender`, e.g. a field with no
 * custom control) keeps working unchanged — nothing there depends on
 * the narrowed single-field type.
 */
interface RhfFormInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  className?: string

  /** Label text shown above the input. Omit to render no label. */
  title?: string

  /** Name of the field in the form, as declared in TFieldValues. */
  id: TName

  /**
   * The `<input>` element's `type` attribute (e.g. "text", "email").
   * Ignored when `control`/`controlRender` are set, since the field
   * is rendered by `controlRender` instead. Use `type="checkbox"` to
   * render a checkbox rather than a text-like input.
   */
  type?: string

  /** Extra text/element rendered inside the label, after the input. */
  inputText?: string

  /** The form's `register` function, from `useForm()`. */
  register: UseFormRegister<TFieldValues>

  /** This field's validation error, from `formState.errors`. */
  error?: FieldError

  /**
   * The form's `control` object, from `useForm()`. Required together
   * with `controlRender` to render a custom controlled component
   * instead of a plain `<input>`.
   */
  control?: Control<TFieldValues>

  /**
   * Renders a custom controlled component for this field (e.g. one
   * with a password-visibility toggle) instead of a plain `<input>`.
   * Requires `control` to also be set.
   */
  controlRender?: ControllerProps<TFieldValues, TName>['render']

  /**
   * Children component sitting on the same line as the input
   */
  children?: any
}

/**
 * @example
 * const { register, control, formState: { errors } } = useForm<LogInType>()
 *
 * // Plain input, no custom control:
 * <RhfFormInput
 *   title="Email"
 *   id="email"
 *   register={register}
 *   error={errors.email}
 * />
 *
 * // Custom controlled input (e.g. a password-visibility toggle):
 * <RhfFormInput
 *   title="Password"
 *   id="password"
 *   register={register}
 *   error={errors.password}
 *   control={control}
 *   controlRender={({ field }) => (
 *     <Password
 *       name={field.name}
 *       value={field.value}
 *       onChange={field.onChange}
 *       onBlur={field.onBlur}
 *     />
 *   )}
 * />
 */
export const RhfFormInput = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: RhfFormInputProps<TFieldValues, TName>
) => (
  <div
    className={
      props.className
        ? `rhf-form-input-block ${props.className}`
        : 'rhf-form-input-block'
    }
  >
    <label htmlFor={props.id} className="rhf-form-input-label">
      {props.title && <div className="rhf-form-input-title">{props.title}</div>}

      {props.control && props.controlRender ? (
        <Controller
          name={props.id}
          control={props.control}
          render={props.controlRender}
        />
      ) : props.type === 'checkbox' ? (
        <input
          type="checkbox"
          id={props.id}
          className="rhf-form-input-checkbox"
          {...props.register(props.id)}
        />
      ) : (
        <div className="rhf-form-input-container">
          <input
            type={props.type}
            id={props.id}
            className="ircam-input rhf-form-input"
            {...props.register(props.id)}
          />
          {props.children}
        </div>
      )}
      {props.inputText}
    </label>
    <div className="rhf-form-input-error">{props.error?.message}</div>
  </div>
)

export const RhfFormInputPassword = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: RhfFormInputProps<TFieldValues, TName>
) => (
  <RhfFormInput
    title={props.title}
    id={props.id}
    register={props.register}
    error={props.error}
    control={props.control}
    controlRender={({ field }) => (
      <Password
        name={field.name}
        value={field.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
      />
    )}
  />
)
